import { DESTINATION_SIDE, SOURCE_SIDE } from "./constants.js"

export function resolveVariant(product, options = {}) {
  if (!product) return null

  const activeVariants = product.variants.filter((variant) => variant.active)
  if (activeVariants.length === 1) return activeVariants[0]

  const selectedOptions = Object.fromEntries(Object.entries(options).filter(([, value]) => value))
  const matches = activeVariants.filter((variant) => {
    const expected = variant.option_values
    return Object.keys(expected).length === Object.keys(selectedOptions).length && Object.entries(expected).every(([name, value]) => selectedOptions[name] === value)
  })

  return matches.length === 1 ? matches[0] : null
}

export function compatibleOptionValues(product, selectedOptions = {}, optionName) {
  if (!product) return []

  const variants = Array.isArray(product) ? product : product.variants
  const otherOptions = Object.fromEntries(Object.entries(selectedOptions).filter(([name]) => name !== optionName))
  return [...new Set(variants.filter((variant) => variant.active && Object.entries(otherOptions).every(([name, value]) => variant.option_values[name] === value)).map((variant) => variant.option_values[optionName]).filter(Boolean))]
}

export function variantOptionKey(optionValues = {}) {
  return JSON.stringify(Object.entries(optionValues).sort(([left], [right]) => left.localeCompare(right)))
}

export function activeVariants(product) {
  return product?.variants?.filter((variant) => variant.active) || []
}

export function activeMappings(product) {
  return (product?.saved_mappings || []).filter((mapping) => mapping.status !== "DISABLED")
}

export function mappedVariantIds(product) {
  const variants = activeVariants(product)
  const mappings = activeMappings(product)
  if (mappings.some((mapping) => mapping.source_variant_id === null)) return variants.map((variant) => variant.id)

  return [...new Set(mappings.map((mapping) => mapping.source_variant_id).filter(Boolean))]
}

export function mappedOptionKeys(product) {
  const mappedIds = new Set(mappedVariantIds(product))
  return activeVariants(product)
    .filter((variant) => mappedIds.has(variant.id))
    .map((variant) => variantOptionKey(variant.option_values))
}

export function isVariantMapped(product, variantId) {
  return mappedVariantIds(product).includes(String(variantId))
}

export function mappedVariantMappings(product) {
  const mappings = activeMappings(product)
  const allVariantsMapping = mappings.find((mapping) => mapping.source_variant_id === null)
  const variantMappings = new Map(mappings.filter((mapping) => mapping.source_variant_id).map((mapping) => [String(mapping.source_variant_id), mapping]))

  return activeVariants(product).flatMap((variant) => {
    const mapping = allVariantsMapping || variantMappings.get(String(variant.id))
    return mapping ? [{ variant, mapping }] : []
  })
}

export function unmappedVariants(product) {
  const mappedIds = new Set(mappedVariantIds(product))
  return activeVariants(product).filter((variant) => !mappedIds.has(variant.id))
}

export function isValidUnitsPerPack(value) {
  if (value === "" || value === null || value === undefined) return false

  const number = Number(value)
  return Number.isInteger(number) && number >= 1
}

export function debounce(callback, delay = 200, setTimeoutFn = setTimeout, clearTimeoutFn = clearTimeout) {
  let timeoutId

  const debounced = (...args) => {
    if (timeoutId) clearTimeoutFn(timeoutId)
    timeoutId = setTimeoutFn(() => {
      timeoutId = undefined
      callback(...args)
    }, delay)
  }

  debounced.cancel = () => {
    if (!timeoutId) return

    clearTimeoutFn(timeoutId)
    timeoutId = undefined
  }

  return debounced
}

export function mergeProducts(existing, incoming) {
  const products = [...existing, ...incoming]
  return products.filter((product, index) => products.findIndex((candidate) => candidate.id === product.id) === index)
}

export function resetMappingState() {
  return {
    [SOURCE_SIDE]: { product_id: null, variant: null, options: {}, select_all: true, has_active_variants: false, mapping_id: null, mapped_variant_ids: [] },
    [DESTINATION_SIDE]: { product_id: null, variant: null, options: {}, select_all: false, has_active_variants: false },
  }
}

export function isMappingReady(source, destination) {
  const sourceReady = source?.product_id && (source.select_all ? source.has_active_variants : source.variant?.id)
  return Boolean(sourceReady && destination?.product_id && destination?.variant?.id)
}

export function buildValidationPayload(source, destination, unitsPerPack = 1) {
  return {
    source_product_id: source.product_id,
    source_variant_id: source.select_all ? null : source.variant?.id,
    source_options: source.select_all ? {} : source.options,
    source_all_variants: source.select_all === true,
    destination_product_id: destination.product_id,
    destination_variant_id: destination.variant?.id,
    destination_options: destination.options,
    units_per_pack: unitsPerPack,
  }
}

export function buildMappingValidationPayload(source, destination) {
  const payload = buildValidationPayload(source, destination)
  delete payload.units_per_pack
  return payload
}
